import { icon } from "@fortawesome/fontawesome-svg-core";
import { faExternalLinkAlt } from "@fortawesome/pro-regular-svg-icons/faExternalLinkAlt";
import { faUsers } from "@fortawesome/pro-solid-svg-icons/faUsers";

const LEAD_VARIABLES = [
  { text: "Contact First Name", value: "{{firstName}}" },
  { text: "Contact Last Name", value: "{{lastName}}" },
  { text: "Contact Full Name", value: "{{fullName}}" },
  { text: "Contact Company", value: "{{company}}" },
  { text: "Agent's Volume", value: "{{Volume}}" },
];

const REFERRER_VARIABLES = [
  { text: "Referrer First", value: "{{referrerFirst}}" },
  { text: "Referrer Last", value: "{{referrerLast}}" },
  { text: "Referrer Full Name", value: "{{referrerName}}" },
];

const SENDER_VARIABLES = [
  { text: "Sender First Name", value: "{{senderFirst}}" },
  { text: "Sender Last Name", value: "{{senderLast}}" },
  { text: "Sender Full Name", value: "{{senderFull}}" },
  { text: "Sender Company", value: "{{senderCompany}}" },
  { text: "Sender Landing Page", value: "{{LandingPage}}" },
  { text: "Sender Phone Number", value: "{{senderPhone}}" },
  { text: "Sender Email Signature", value: "{{senderEmailSignature}}" },
];

const OWNER_VARIABLES = [
  { text: "Owner Full Name", value: "{{ownerName}}" },
  { text: "Owner First Name", value: "{{ownerFirst}}" },
  { text: "Owner Last Name", value: "{{ownerLast}}" },
  { text: "Owner Email", value: "{{ownerEmail}}" },
  {
    text: "Owner Phone",
    value: "{{ownerPhone}}",
    tooltip: "Owner Phone uses the owner's BrokerKit number for this team.",
  },
  {
    text: "Owner Company",
    value: "{{ownerCompany}}",
    tooltip: "Owner Company uses your account Office or Team name.",
  },
];

const ALERT_FIELDS = [
  { text: "Full Address", field: "fullAddress" },
  { text: "Street Address", field: "streetAddress" },
  { text: "City", field: "city" },
  { text: "Price", field: "price" },
  { text: "Alert Type", field: "alertType" },
];

const ALERT_VARIABLE_GROUPS = [
  { text: "Last Closing", group: "closing" },
  { text: "Last Listing", group: "listing" },
  { text: "Last Pending", group: "pending" },
  { text: "Last Contract", group: "contract" },
];

const senderVariables = (includeEmailSignature) =>
  SENDER_VARIABLES.filter(
    ({ value }) =>
      includeEmailSignature || value !== "{{senderEmailSignature}}",
  );

const alertVariables = ALERT_VARIABLE_GROUPS.map(({ text, group }) => ({
  text,
  variables: ALERT_FIELDS.map(({ text: fieldText, field }) => ({
    text: fieldText,
    value: `{{${group}.${field}}}`,
  })),
}));

const buildVariableGroups = ({
  includeSender = true,
  includeEmailSignature = true,
  includeOwner = false,
  includeAlertVariables = true,
} = {}) => {
  const groups = [{ text: "Contact", variables: LEAD_VARIABLES }];

  if (includeOwner) {
    groups.push({
      text: "Owner",
      tooltip:
        "Owner variables use the contact owner or task owner for this workflow.",
      variables: OWNER_VARIABLES,
    });
  }

  if (includeSender) {
    groups.push({
      text: "Sender",
      variables: senderVariables(includeEmailSignature),
    });
  }

  groups.push({ text: "Referrer", variables: REFERRER_VARIABLES });

  if (includeAlertVariables) {
    groups.push({
      text: "Alert",
      groups: alertVariables,
    });
  }

  return groups;
};

const menuItemsForVariables = (editor, variables) =>
  variables.map((variable) => ({
    type: "menuitem",
    text: variable.text,
    tooltip: variable.tooltip,
    onAction: () => editor.insertContent(variable.value),
  }));

const buildMenuItems = (groups, editor) =>
  groups.map(({ text, tooltip, variables, groups: nestedGroups }) => ({
    type: "nestedmenuitem",
    text,
    tooltip,
    getSubmenuItems: () =>
      nestedGroups
        ? buildMenuItems(nestedGroups, editor)
        : menuItemsForVariables(editor, variables),
  }));

const EditorHelpers = {
  variableGroups: buildVariableGroups,

  addVariablesMenu: (
    editor,
    {
      includeSender = true,
      includeEmailSignature = true,
      includeOwner = false,
      includeAlertVariables = true,
    } = {},
  ) => {
    editor.ui.registry.addIcon("usersIcon", icon(faUsers).html[0]);

    const groups = buildVariableGroups({
      includeSender,
      includeEmailSignature,
      includeOwner,
      includeAlertVariables,
    });

    editor.ui.registry.addMenuButton("variables", {
      icon: "usersIcon",
      text: "Variables...",
      tooltip: "Variables",
      fetch: (callback) => {
        callback(buildMenuItems(groups, editor));
      },
    });
  },

  addVisitLink: (editor) => {
    editor.on("OpenWindow", (e) => {
      const dialogHeaderSelector = 'div[role="dialog"] .tox-dialog__header';
      const titleElement = document.querySelector(
        `${dialogHeaderSelector} .tox-dialog__title`,
      );
      if (titleElement?.textContent === "Insert/Edit Link") {
        const url = e.dialog.getData().url?.value;
        if (!url) {
          return;
        }

        const iconHtml = icon(faExternalLinkAlt).html[0];
        const visitLinkButtonHtml = `<a href=${url} target="_blank" class="btn btn-link" style="right: 60px; position: absolute;"> Visit Link ${iconHtml}</a>`;
        const closeButton = document.querySelector(
          `${dialogHeaderSelector} button[data-mce-name="close"].tox-button`,
        );
        closeButton.insertAdjacentHTML("beforebegin", visitLinkButtonHtml);
      }
    });
  },
};

export default EditorHelpers;
